
import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  publicPage?: boolean;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'dark' | 'light';
  systemTheme: 'dark' | 'light';
  isPublicPage: boolean;
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
  resolvedTheme: 'light',
  systemTheme: 'light',
  isPublicPage: false,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'directpress-ui-theme',
  publicPage = false,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );
  const [systemTheme, setSystemTheme] = useState<'dark' | 'light'>(
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );
  
  // Instead of using useLocation, we now accept publicPage as a prop
  const isPublicPage = publicPage;
  
  // On public pages, always use light theme
  const pageTheme = isPublicPage ? 'light' : theme;
  
  // Calculate the resolved theme (what's actually applied)
  const resolvedTheme = pageTheme === 'system' ? systemTheme : pageTheme;

  // Update the document class and local storage when theme changes
  useEffect(() => {
    localStorage.setItem(storageKey, theme);
    
    const root = window.document.documentElement;
    
    // Remove previous theme class
    root.classList.remove('dark', 'light');
    
    // Add the current theme class
    root.classList.add(resolvedTheme);
    
    // Update the color scheme meta tag
    document
      .querySelector('meta[name="color-scheme"]')
      ?.setAttribute('content', resolvedTheme);
  }, [theme, resolvedTheme, storageKey, isPublicPage]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      setSystemTheme(mediaQuery.matches ? 'dark' : 'light');
    };
    
    // Initialize
    handleChange();
    
    // Add listener
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const value = {
    theme,
    setTheme,
    resolvedTheme,
    systemTheme,
    isPublicPage,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};
